import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Users, Package } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card } from "@/components/ui/card";
import QRCode from "qrcode.react";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";

const products = {
  "1": {
    id: "1",
    name: "Hout en Parel Armband",
    price: 3.50,
    description: "Een elegante armband met parels en houten kralen, perfect voor elke gelegenheid.",
    images: [
      "/lovable-uploads/afe85108-72bb-46ab-8d23-e47c1361ac05.png",
    ],
    category: "armbandjes",
    stock: 5,
  },
  "2": {
    id: "2",
    name: "Groen gestreepte ring",
    price: 2.00,
    description: "Een stijlvolle groen gestreepte ring die perfect is voor dagelijks gebruik.",
    images: [
      "/lovable-uploads/432539b9-c41c-4839-98bc-1e4c901bf214.png",
    ],
    category: "ringen",
    stock: 4,
  },
  "3": {
    id: "3",
    name: "Bubbelthee Oorbellen",
    price: 4.50,
    description: "Kleurrijke bubbel oorbellen die je outfit een speelse touch geven.",
    images: [
      "/lovable-uploads/9ee4e88c-d9eb-441a-b8ae-8d247bb3283d.png",
    ],
    category: "oorbellen",
    stock: 2,
  },
  "4": {
    id: "4",
    name: "Lollypop Dream Oorbellen",
    price: 5,
    description: "Schattige eendjes oorbellen die geluk brengen aan de drager.",
    images: [
      "/lovable-uploads/27387ea5-a8ec-4735-a1e6-9663793c6166.png",
    ],
    category: "oorbellen",
  },
  "6": {
    id: "6",
    name: "Ocean Waves Armband",
    price: 13.99,
    description: "Elegante armband geïnspireerd door de golven van de oceaan.",
    images: [
      "/lovable-uploads/816c5780-90e6-435c-9c23-3f54fb9147ba.png",
    ],
    category: "armbandjes",
  },
};

const Product = () => {
  const { id } = useParams();
  const product = id ? products[id] : null;
  const whatsappUrl = "https://chat.whatsapp.com/IpxCmAONN8fKzNlVstNsg5";
  const isMobile = useIsMobile();
  const [viewers, setViewers] = useState(0);

  useEffect(() => {
    const randomViewers = Math.floor(Math.random() * 8) + 1;
    setViewers(randomViewers);
  }, []);

  if (!product) {
    return (
      <>
        <Helmet>
          <title>Product niet gevonden - FLOM</title>
          <meta name="description" content="Het gezochte product kon niet worden gevonden." />
        </Helmet>
        <div className="min-h-screen bg-jewelry-background py-16">
          <div className="container mx-auto px-4">
            <Link 
              to="/" 
              className="inline-flex items-center mb-8 text-jewelry-gold hover:text-jewelry-text transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Terug naar overzicht
            </Link>
            <p className="text-jewelry-text">Product niet gevonden</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${product.name} - FLOM Sieraden`}</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={`${product.name} - FLOM Sieraden`} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={`${window.location.origin}${product.images[0]}`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="product" />
        <meta property="product:price:amount" content={product.price.toString()} />
        <meta property="product:price:currency" content="EUR" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="min-h-screen bg-jewelry-background py-16">
        <div className="container mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center mb-8 text-jewelry-gold hover:text-jewelry-text transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar overzicht
          </Link>
          
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="space-y-6">
              <Card className="overflow-hidden border-2 border-jewelry-gold/20 hover:border-jewelry-gold/40 transition-colors">
                <AspectRatio ratio={1 / 1} className="bg-white">
                  <img
                    src={product.images[0]}
                    alt={`${product.name} - Hoofdafbeelding`}
                    className="object-cover w-full h-full"
                  />
                </AspectRatio>
              </Card>
              
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((image, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer border-2 border-transparent hover:border-jewelry-gold/40 transition-all"
                  >
                    <AspectRatio ratio={1 / 1} className="bg-white">
                      <img
                        src={image}
                        alt={`${product.name} - Afbeelding ${index + 1}`}
                        className="object-cover w-full h-full hover:scale-110 transition-transform duration-300"
                      />
                    </AspectRatio>
                  </Card>
                ))}
              </div>
            </div>

            <div className="space-y-6 lg:p-6">
              <h1 className="text-4xl font-serif font-light text-jewelry-text">{product.name}</h1>
              <p className="text-2xl text-jewelry-gold">€{product.price.toFixed(2)}</p>
              <div className="space-y-2">
                <div className="flex items-center text-jewelry-text/80 text-sm">
                  <Package className="w-4 h-4 mr-2" />
                  <span className="font-medium text-red-500">
                    {product.stock === 1 ? "Nog maar 1 op voorraad!" : `Nog maar ${product.stock || 1} op voorraad!`}
                  </span>
                </div>
                <div className="flex items-center text-jewelry-text/80 text-sm">
                  <Users className="w-4 h-4 mr-2" />
                  <span>{viewers} mensen bekijken dit product</span>
                </div>
              </div>
              <p className="text-jewelry-text leading-relaxed">{product.description}</p>
              
              <div className="mt-8 p-6 bg-white rounded-lg shadow-sm border-2 border-jewelry-gold/20">
                <h3 className="text-lg font-medium text-jewelry-text mb-4">Bestellen via WhatsApp</h3>
                <div className="flex flex-col items-center space-y-4">
                  {isMobile ? (
                    <Button
                      className="bg-[#25D366] hover:bg-[#128C7E] text-white font-medium px-8 py-3 rounded-full shadow-lg transition-colors"
                      onClick={() => window.open(whatsappUrl, '_blank')}
                    >
                      Bestel via WhatsApp
                    </Button>
                  ) : (
                    <>
                      <QRCode 
                        value={whatsappUrl}
                        size={200}
                        level="H"
                        includeMargin={true}
                        className="mx-auto"
                      />
                      <p className="text-sm text-jewelry-text text-center">
                        Scan deze QR-code om ons te volgen op WhatsApp en direct te bestellen!
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Product;
